import { SafeHTML } from '@thoughtspot/radiant-react/widgets/safe-html';
import DOMPurify from 'dompurify';
import React, { useLayoutEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import styles from '../../styles/tooltip.module.scss';
const BLOCKED_PROPERTIES = new Set(['pointer-events', 'all']);
const BLOCKED_POSITION_VALUES = new Set(['fixed', 'sticky']);
const BLOCKED_FUNCTIONS = [
    'url(',
    'image-set(',
    'src(',
    'image(',
    'cross-fade(',
    'element(',
    'paint(',
];
const parseTopLevelRules = (css) => {
    const rules = [];
    let prelude = '';
    let i = 0;
    let quote = null;
    while (i < css.length) {
        const char = css[i];
        if (quote) {
            prelude += char;
            if (char === quote) {
                quote = null;
            }
            i += 1;
        }
        else if (char === '"' || char === "'") {
            quote = char;
            prelude += char;
            i += 1;
        }
        else if (char === '{') {
            let depth = 1;
            let j = i + 1;
            let bodyQuote = null;
            while (j < css.length && depth > 0) {
                const bodyChar = css[j];
                if (bodyQuote) {
                    if (bodyChar === bodyQuote) {
                        bodyQuote = null;
                    }
                }
                else if (bodyChar === '"' || bodyChar === "'") {
                    bodyQuote = bodyChar;
                }
                else if (bodyChar === '{') {
                    depth += 1;
                }
                else if (bodyChar === '}') {
                    depth -= 1;
                }
                j += 1;
            }
            const end = depth > 0 ? css.length : j - 1;
            rules.push({
                prelude: prelude.trim(),
                body: css.slice(i + 1, end),
            });
            prelude = '';
            i = j;
        }
        else if (char === ';') {
            rules.push({ prelude: prelude.trim(), body: null });
            prelude = '';
            i += 1;
        }
        else {
            prelude += char;
            i += 1;
        }
    }
    if (prelude.trim()) {
        rules.push({ prelude: prelude.trim(), body: null });
    }
    return rules;
};
const splitDeclarations = (body) => {
    const declarations = [];
    let current = '';
    let quote = null;
    for (const char of body) {
        if (quote) {
            current += char;
            if (char === quote) {
                quote = null;
            }
        }
        else if (char === '"' || char === "'") {
            quote = char;
            current += char;
        }
        else if (char === ';') {
            declarations.push(current);
            current = '';
        }
        else {
            current += char;
        }
    }
    declarations.push(current);
    return declarations;
};
const sanitizeDeclarations = (body) => {
    const kept = [];
    splitDeclarations(body).forEach(declaration => {
        const separator = declaration.indexOf(':');
        if (separator === -1) {
            return;
        }
        const property = declaration.slice(0, separator).trim();
        const value = declaration.slice(separator + 1).trim();
        if (!property || !value) {
            return;
        }
        if (property.includes('\\') || value.includes('\\')) {
            return;
        }
        const lowerProperty = property.toLowerCase();
        const lowerValue = value
            .toLowerCase()
            .replace(/!\s*important\s*$/, '')
            .trim();
        if (BLOCKED_PROPERTIES.has(lowerProperty)) {
            return;
        }
        if (lowerProperty === 'position' &&
            BLOCKED_POSITION_VALUES.has(lowerValue)) {
            return;
        }
        if (BLOCKED_FUNCTIONS.some(fn => lowerValue.includes(fn))) {
            return;
        }
        kept.push(`${property}: ${value}`);
    });
    return kept;
};
const sanitizeRuleList = (css) => parseTopLevelRules(css)
    .map(({ prelude, body }) => {
    if (!prelude || prelude.startsWith('@') || body === null) {
        return '';
    }
    const declarations = sanitizeDeclarations(body);
    return declarations.length
        ? `${prelude} { ${declarations.join('; ')}; }`
        : '';
})
    .filter(Boolean)
    .join('\n');
export const sanitizeTooltipCss = (css) => {
    if (!css) {
        return '';
    }
    return sanitizeRuleList(css.replace(/\/\*[\s\S]*?\*\//g, ''));
};
const getPortalTarget = () => {
    const fullscreenHost = document.fullscreenElement ??
        document
            .webkitFullscreenElement;
    return fullscreenHost instanceof HTMLElement
        ? fullscreenHost
        : document.body;
};
const ShadowContent = ({ html, css, }) => {
    const hostRef = useRef(null);
    const sanitizedCss = useMemo(() => sanitizeTooltipCss(css), [css]);
    useLayoutEffect(() => {
        const host = hostRef.current;
        if (!host) {
            return;
        }
        const shadowRoot = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
        const style = document.createElement('style');
        style.textContent = sanitizedCss;
        const content = document.createElement('div');
        content.innerHTML = DOMPurify.sanitize(html, {
            FORBID_TAGS: ['style'],
        });
        content.querySelectorAll('[style]').forEach(element => {
            const declarations = sanitizeDeclarations(element.getAttribute('style') ?? '');
            if (declarations.length) {
                element.setAttribute('style', `${declarations.join('; ')};`);
            }
            else {
                element.removeAttribute('style');
            }
        });
        shadowRoot.textContent = '';
        shadowRoot.append(style, content);
    }, [html, sanitizedCss]);
    return React.createElement("div", { ref: hostRef });
};
export const TooltipComponent = ({ isVisible, tooltipConfig, tooltipEl, }) => {
    const positionTooltip = (positionAttributes) => {
        if (!tooltipEl.current) {
            return;
        }
        Object.entries(positionAttributes).forEach(([attr, value]) => {
            tooltipEl.current.style[attr] =
                value == null ? '' : `${value}px`;
        });
        tooltipEl.current.style.display = 'block';
    };
    useLayoutEffect(() => {
        if (tooltipConfig && isVisible) {
            positionTooltip(tooltipConfig);
        }
    }, [isVisible, tooltipConfig]);
    const isChartStyled = tooltipConfig.customTooltipCss !== undefined;
    if (!isVisible) {
        return null;
    }
    if (!isChartStyled) {
        return (React.createElement("div", { className: styles.customTooltip, ref: tooltipEl },
            React.createElement("ul", null,
                Object.entries(tooltipConfig.content).map(([key, value]) => (React.createElement("li", { key: key, className: styles.customTooltipRow },
                    React.createElement("span", { className: styles.bkKey }, key),
                    React.createElement("span", { className: styles.bkValue }, value)))),
                tooltipConfig.customTooltipContent.map((value, index) => {
                    return (React.createElement("li", { key: `${index}-${value}`, className: styles.customTooltipRow },
                        React.createElement("span", { className: styles.bkValue },
                            React.createElement(SafeHTML, { html: value }))));
                }))));
    }
    return ReactDOM.createPortal(React.createElement("div", { className: styles.chromelessTooltip, ref: tooltipEl },
        React.createElement(ShadowContent, { html: tooltipConfig.customTooltipContent.join(''), css: tooltipConfig.customTooltipCss ?? '' })), getPortalTarget());
};
//# sourceMappingURL=tooltip.js.map